import React from "react";

import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import Header from "@cloudscape-design/components/header";
import Tabs from "@cloudscape-design/components/tabs";
import Spinner from "@cloudscape-design/components/spinner";
import Badge from "@cloudscape-design/components/badge";
import Table from "@cloudscape-design/components/table";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Select from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import Multiselect from "@cloudscape-design/components/multiselect";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Grid from "@cloudscape-design/components/grid";
import RadioGroup from "@cloudscape-design/components/radio-group";
import FormField from "@cloudscape-design/components/form-field";
import Container from "@cloudscape-design/components/container";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { userStore } from "../stores/access";
import { staffStore } from "../stores/staff";
import { staffDetailModalStore } from "../stores/staffDetailModal";
import { staffSessionsModalStore } from "../stores/staffSessionsModal";
/* ------- */

class StaffDetailModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			showAccessPassword: false,
			showCredentialsPassword: false,
			accessMatrixExpanded: false,
		};
		this.userContextVersion = 0;
	}

	componentDidMount() {
		// Modal is gated by show=true in ModalManager, so mount == first open.
		this.loadPerson();
		const modal = this.props.staffDetailModal || {};
		if (modal.activeTabId === "access" && !modal.groupsLoaded && !modal.groupsError) {
			this.loadGroups();
		}
	}

	componentDidUpdate(prevProps) {
		const prevModal = prevProps.staffDetailModal || {};
		const modal = this.props.staffDetailModal || {};
		const prevUserLogin = prevProps.user && prevProps.user.login;
		const currentUserLogin = this.props.user && this.props.user.login;
		if (prevUserLogin !== currentUserLogin) {
			this.resetModal();
			return;
		}
		if (modal.show && modal.login && modal.login !== prevModal.login && this.lastLoadLogin !== modal.login) {
			this.loadPerson(modal.login, { resetTfaReveal: true });
		}
	}

	getDefaultAccessActionForm() {
		return { mode: "update", brands: [], level: "", password: "", groupIds: [], updateLevel: false, updateGroups: true, groupUpdateMode: "replace" };
	}

	buildFormsFromPerson() {
		return {
			accessActionForm: this.getDefaultAccessActionForm(),
		};
	}

	getDefaultPending() {
		return {
			accessAction: false,
			saveCredentials: false,
			changeLogin: false,
			changePassword: false,
			resetTfa: {},
			removeTfa: false,
			getTfa: false,
		};
	}

	getPending(modal) {
		const pending = (modal && modal.pending) || {};
		return {
			...this.getDefaultPending(),
			...pending,
			resetTfa: { ...(pending.resetTfa || {}) },
		};
	}

	updateModal(updater) {
		this.props.setStaffDetailModal((modal) => {
			const current = modal || {};
			if (!current.show) return current;
			const patch = typeof updater === "function" ? updater(current) : updater;
			if (!patch) return current;
			return { ...current, ...patch };
		});
	}

	loadPerson(loginOverride, options) {
		const modal = this.props.staffDetailModal || {};
		const login = typeof loginOverride === "string" && loginOverride.length ? loginOverride : modal.login;
		if (!modal.show || !login) return;
		const resetTfaReveal = options && options.resetTfaReveal;
		const loadUserContextVersion = this.userContextVersion;
		this.lastLoadLogin = login;
		this.updateModal((modal) => ({
			login,
			loaded: false,
			...(resetTfaReveal
				? {
						tfaReveal: null,
						pending: { ...this.getPending(modal), getTfa: false },
				  }
				: {}),
		}));
		this.props.Interface.request("get", { class: "settings", module: "staff", method: "person" }, { login: login })
			.then((person) => {
				const forms = this.buildFormsFromPerson(person);
				this.updateModal((modal) => {
					if (!modal.show || modal.login !== login || this.userContextVersion !== loadUserContextVersion) return null;
					return {
						login,
						person,
						loaded: true,
						error: { text: "", code: 0, timestamp: 0 },
						...forms,
					};
				});
			})
			.catch((e) => {
				this.updateModal((modal) => {
					if (!modal.show || modal.login !== login || this.userContextVersion !== loadUserContextVersion) return null;
					return { loaded: true, error: e };
				});
			});
	}

	loadGroups() {
		if (this.props.staffDetailModal.groupsLoaded && !this.props.staffDetailModal.groupsError) return;
		const loadUserContextVersion = this.userContextVersion;
		this.updateModal({
			groupsLoaded: false,
			groupsError: null,
		});
		this.props.Interface.request("get", { class: "settings", module: "staff", method: "groups" }, {})
			.then((groups) => {
				if (this.userContextVersion !== loadUserContextVersion) return;
				this.updateModal({
					groups: Array.isArray(groups) ? groups : [],
					groupsLoaded: true,
					groupsError: null,
				});
			})
			.catch((e) => {
				if (this.userContextVersion !== loadUserContextVersion) return;
				const text = "Failed to load groups: " + (e && e.text ? e.text : "unknown");
				this.updateModal({
					groups: [],
					groupsLoaded: true,
					groupsError: text,
				});
				this.props.Interface.addNoty("Error", text);
			});
	}

	getDefaultModalState() {
		return {
			show: false,
			loaded: false,
			error: { text: "", code: 0, timestamp: 0 },
			login: "",
			person: null,
			dirty: false,
			activeTabId: "overview",
			groups: [],
			groupsLoaded: false,
			groupsError: null,
			accessActionForm: this.getDefaultAccessActionForm(),
			credentialsForm: {
				newLogin: "",
				password: "",
				passwordScope: "global",
				passwordBrands: [],
			},
			tfaReveal: null,
			pending: this.getDefaultPending(),
		};
	}

	resetModal() {
		this.userContextVersion += 1;
		this.lastLoadLogin = "";
		this.setState({
			showAccessPassword: false,
			showCredentialsPassword: false,
			accessMatrixExpanded: false,
		});
		this.props.setStaffDetailModal(this.getDefaultModalState());
	}

	formatLastOnline(lastOnline) {
		if (!lastOnline) {
			return (
				<Box variant="span" color="text-status-error">
					Never
				</Box>
			);
		}
		if (Date.now() - lastOnline < 300000) {
			return (
				<Box variant="span" color="text-status-success">
					Online
				</Box>
			);
		}
		return new Date(lastOnline).toLocaleTimeString() + " " + new Date(lastOnline).toLocaleDateString();
	}

	onDismiss() {
		if (this.props.staffDetailModal.dirty) {
			this.props.setStaff({
				...this.props.staff,
				loaded: false,
				reloadSignal: (this.props.staff.reloadSignal || 0) + 1,
			});
		}
		this.resetModal();
	}

	renderKeyValue(label, value) {
		return (
			<div>
				<Box variant="awsui-key-label">{label}</Box>
				<Box variant="span">{value}</Box>
			</div>
		);
	}

	selectAllOption() {
		return { value: "__select_all_brands__", label: "Select all" };
	}

	isSelectAllOption(option) {
		return option && option.value === this.selectAllOption().value;
	}

	isAllOptionsSelected(options, selectedOptions) {
		const optionList = Array.isArray(options) ? options : [];
		const optionValues = {};
		optionList.forEach((option) => {
			if (option && option.value != null) optionValues[String(option.value)] = true;
		});
		const selectedCount = (Array.isArray(selectedOptions) ? selectedOptions : []).filter(
			(option) => option && optionValues[String(option.value)],
		).length;
		return optionList.length > 0 && selectedCount === optionList.length;
	}

	optionsWithSelectAll(options) {
		const optionList = Array.isArray(options) ? options : [];
		return optionList.length ? [this.selectAllOption(), ...optionList] : optionList;
	}

	selectedOptionsWithSelectAll(options, selectedOptions) {
		const selected = Array.isArray(selectedOptions) ? selectedOptions : [];
		return this.isAllOptionsSelected(options, selected) ? [this.selectAllOption(), ...selected] : selected;
	}

	resolveSelectAllChange(nextSelectedOptions, options, previousSelectedOptions) {
		const optionList = Array.isArray(options) ? options : [];
		const optionValues = {};
		optionList.forEach((option) => {
			if (option && option.value != null) optionValues[String(option.value)] = true;
		});
		const selected = Array.isArray(nextSelectedOptions) ? nextSelectedOptions : [];
		const hasSelectAll = selected.some((option) => this.isSelectAllOption(option));
		const nextOptions = selected.filter((option) => option && optionValues[String(option.value)]);
		const wasAllSelected = this.isAllOptionsSelected(optionList, previousSelectedOptions);

		if (!optionList.length) return [];
		if (hasSelectAll && !wasAllSelected) return optionList;
		if (!hasSelectAll && wasAllSelected && nextOptions.length === optionList.length) return [];
		return nextOptions;
	}

	renderOptionTiles(selected, disabled, emptyText, onRemove) {
		const editingDisabled = !!disabled;
		return selected.length ? (
			<div
				style={{
					display: "grid",
					gap: "6px",
					gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
				}}>
				{selected.map((option) => (
					<div
						key={option.value}
						role="button"
						tabIndex={editingDisabled ? -1 : 0}
						title={editingDisabled ? option.label : "Remove " + option.label}
						aria-disabled={editingDisabled}
						onClick={() => {
							if (editingDisabled) return;
							onRemove(selected.filter((o) => o.value !== option.value));
						}}
						onKeyDown={(e) => {
							if (editingDisabled) return;
							if (e.key !== "Enter" && e.key !== " ") return;
							e.preventDefault();
							onRemove(selected.filter((o) => o.value !== option.value));
						}}
						style={{
							border: "1px solid #0972d3",
							borderRadius: "8px",
							boxSizing: "border-box",
							cursor: editingDisabled ? "default" : "pointer",
							display: "flex",
							gap: "8px",
							justifyContent: "space-between",
							minHeight: "36px",
							overflow: "hidden",
							padding: "7px 10px",
							width: "100%",
						}}>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
						{editingDisabled ? null : <span aria-hidden="true">x</span>}
					</div>
				))}
			</div>
		) : (
			<Box variant="small" color="text-body-secondary">
				{emptyText}
			</Box>
		);
	}

	renderOverview() {
		const { loaded, error, person } = this.props.staffDetailModal;

		if (loaded && error && error.text && error.text.length) {
			return (
				<Box margin={{ top: "m" }}>
					<Alert
						type="error"
						header="Error loading data:"
						children={
							<>
								<Box variant="p" children={error.text} />
								<hr />
								<Box variant="p">
									request error code: {error.code} | request timestamp: {error.timestamp}
								</Box>
							</>
						}
					/>
				</Box>
			);
		}

		if (!loaded) {
			return (
				<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
					<Spinner size="large" />
				</Box>
			);
		}

		if (!person) {
			return <></>;
		}

		const granted = person.projects || [];

		return (
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h3" description="Main persona information and current status.">
							Summary
						</Header>
					}>
					<ColumnLayout columns={3} variant="text-grid">
						{this.renderKeyValue("Login", person.login)}
						{this.renderKeyValue("Level (max)", person.level)}
						{this.renderKeyValue("Active", person.active ? <Badge color="green">Yes</Badge> : <Badge color="grey">No</Badge>)}
						{this.renderKeyValue("2FA enabled", person.hasTfa ? <Badge color="green">Yes</Badge> : <Badge color="grey">No</Badge>)}
						{this.renderKeyValue("Granted brands", granted.length)}
						{this.renderKeyValue("Created", person.created ? new Date(person.created).toLocaleDateString() : "")}
						{this.renderKeyValue("Last online", this.formatLastOnline(person.lastOnline))}
					</ColumnLayout>
				</Container>

				<Container
					header={
						<Header variant="h3" description="Granted brand rows and quick access to per-brand sessions.">
							Projects and actions
						</Header>
					}>
					<Table
						wrapLines={false}
						items={granted}
						columnDefinitions={[
							{
								id: "brand",
								header: "Brand",
								cell: (item) => <Box variant="span">{item.brandName}</Box>,
							},
							{
								id: "level",
								header: "Level",
								cell: (item) => (item.level === 0 ? <Badge color="grey">disabled</Badge> : item.level),
							},
							{
								id: "lastOnline",
								header: "Last online",
								cell: (item) => this.formatLastOnline(item.lastOnline),
							},
							{
								id: "created",
								header: "Created",
								cell: (item) => (item.created ? new Date(item.created).toLocaleDateString() : ""),
							},
							{
								id: "actions",
								header: "Sessions",
								width: 120,
								cell: (item) => (
									<Button wrapText={false} ariaLabel="Open sessions" onClick={() => this.onOpenSessions(item)}>
										Open
									</Button>
								),
							},
						]}
						empty="No brands granted"
					/>
				</Container>
			</SpaceBetween>
		);
	}

	clampLevel(raw) {
		const callerLevel = Number(this.props.user.level) || 0;
		const max = Math.max(0, callerLevel - 1);
		let n = parseInt(raw, 10);
		if (!Number.isFinite(n)) return "";
		if (n < 0) n = 0;
		if (n > max) n = max;
		return String(n);
	}

	clampGrantLevel(raw) {
		const callerLevel = Number(this.props.user.level) || 0;
		const max = Math.max(0, callerLevel - 1);
		if (max < 1) return "";
		let n = parseInt(raw, 10);
		if (!Number.isFinite(n)) return "";
		if (n < 1) n = 1;
		if (n > max) n = max;
		return String(n);
	}

	updateAccessActionForm(patch) {
		this.updateModal((modal) => ({
			accessActionForm: { ...this.getDefaultAccessActionForm(), ...(modal.accessActionForm || {}), ...patch },
		}));
	}

	getRequestErrorText(error, fallback) {
		return (error && error.text) || fallback || "unknown";
	}

	isCurrentUserContext(userContextVersion) {
		return this.userContextVersion === userContextVersion;
	}

	formatBatchFailures(failures) {
		return failures.map((failure) => failure.brandName + ": " + failure.error).join("; ");
	}

	onSubmitAccessAction() {
		const state = this.props.staffDetailModal;
		const person = state.person;
		if (!person) return;
		const form = { ...this.getDefaultAccessActionForm(), ...(state.accessActionForm || {}) };
		const mode = form.mode || "update";
		const needsGroupsLoaded = mode === "grant" || mode === "restore" || (mode === "update" && !!form.updateGroups);
		if (needsGroupsLoaded && (state.groupsError || !state.groupsLoaded)) {
			this.props.Interface.addNoty("Error", state.groupsError || "Groups are still loading");
			return;
		}
		if (mode === "update" && !form.updateLevel && !form.updateGroups) {
			this.props.Interface.addNoty("Error", "Select level and/or groups to update");
			return;
		}
		const groupUpdateMode = form.groupUpdateMode === "add" || form.groupUpdateMode === "remove" ? form.groupUpdateMode : "replace";
		if (mode === "update" && form.updateGroups && groupUpdateMode !== "replace" && !(form.groupIds || []).length) {
			this.props.Interface.addNoty("Error", "Select at least one group to " + groupUpdateMode);
			return;
		}
		const selectedBrands = [];
		(form.brands || []).forEach((option) => {
			const brand = Number(option.value);
			if (Number.isFinite(brand) && !selectedBrands.includes(brand)) selectedBrands.push(brand);
		});
		if (!selectedBrands.length) {
			this.props.Interface.addNoty("Error", "Select at least one brand");
			return;
		}
		const callerLevel = Number(this.props.user.level) || 0;
		const lvl = Number(form.level);
		const needsLevel = mode !== "disable" && (mode !== "update" || !!form.updateLevel);
		if (needsLevel && (!Number.isFinite(lvl) || lvl < 1 || lvl >= callerLevel)) {
			this.props.Interface.addNoty("Error", "Level must be 1..." + (callerLevel - 1));
			return;
		}
		const isSelf = this.props.user && this.props.user.login && person.login === this.props.user.login;
		if (isSelf) {
			this.props.Interface.addNoty("Error", "You cannot modify your own per-brand access");
			return;
		}
		const password = typeof form.password === "string" ? form.password : "";
		if ((mode === "grant" || mode === "restore") && (password.length < 8 || password.length > 128)) {
			this.props.Interface.addNoty("Error", "Password must be 8..128 characters");
			return;
		}
		if (mode === "disable") {
			// eslint-disable-next-line no-alert
			if (
				!window.confirm(
					"Disable access for " +
						selectedBrands.length +
						" selected brand" +
						(selectedBrands.length === 1 ? "" : "s") +
						"? Backend will clear groups and expire active sessions for each selected brand. This batch action applies to all selected brands."
				)
			)
				return;
		}

		const projects = person.projects || [];
		const available = person.availableBrands || [];
		const getBrandName = (brand) => {
			const project = projects.find((p) => Number(p.brand) === Number(brand));
			if (project) return project.brandName;
			const availableBrand = available.find((p) => Number(p.brand) === Number(brand));
			return availableBrand ? availableBrand.brandName : "Brand";
		};
		const selectedGroupIds = (form.groupIds || []).map((value) => Number(value)).filter((value) => Number.isFinite(value));
		const grantGroupPayload = selectedGroupIds.map((value) => ({ value: value }));
		const getUpdateGroupPayload = (brand) => {
			if (groupUpdateMode === "replace") return grantGroupPayload;
			const project = projects.find((p) => Number(p.brand) === Number(brand));
			const currentIds = ((project && project.groups) || []).map((group) => Number(group.id)).filter((value) => Number.isFinite(value));
			const nextIds =
				groupUpdateMode === "add"
					? Array.from(new Set([...currentIds, ...selectedGroupIds]))
					: currentIds.filter((value) => !selectedGroupIds.includes(value));
			return nextIds.map((value) => ({ value: value }));
		};
		const failures = [];
		let successCount = 0;
		const actionUserContextVersion = this.userContextVersion;

		this.updateModal((modal) => ({
			pending: { ...this.getPending(modal), accessAction: true },
		}));

		const runUpdate = (brand) => {
			if (!this.isCurrentUserContext(actionUserContextVersion)) return Promise.resolve();
			const brandFailures = [];
			let changed = false;
			let chain = Promise.resolve();
			if (form.updateLevel) {
				chain = chain.then(() => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return null;
					return this.props.Interface
						.request("set", { class: "settings", module: "staff", method: "accessBrand" }, { login: person.login, brand, level: lvl })
						.then(() => {
							if (!this.isCurrentUserContext(actionUserContextVersion)) return;
							changed = true;
						})
						.catch((e) => {
							if (!this.isCurrentUserContext(actionUserContextVersion)) return;
							brandFailures.push("level: " + this.getRequestErrorText(e, "failed"));
						});
				});
			}
			if (form.updateGroups) {
				chain = chain.then(() => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return null;
					return this.props.Interface
						.request("set", { class: "settings", module: "staff", method: "groupsUpdate" }, { login: person.login, brand, groups: getUpdateGroupPayload(brand) })
						.then(() => {
							if (!this.isCurrentUserContext(actionUserContextVersion)) return;
							changed = true;
						})
						.catch((e) => {
							if (!this.isCurrentUserContext(actionUserContextVersion)) return;
							brandFailures.push("groups: " + this.getRequestErrorText(e, "failed"));
						});
				});
			}
			return chain.then(() => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				if (changed) successCount += 1;
				if (brandFailures.length) {
					failures.push({ brand, brandName: getBrandName(brand), error: brandFailures.join(", ") });
				}
			});
		};

		const runGrant = (brand) => {
			if (!this.isCurrentUserContext(actionUserContextVersion)) return Promise.resolve();
			return this.props.Interface
				.request("set", { class: "settings", module: "staff", method: "grantBrand" }, { login: person.login, brand, level: lvl, groups: grantGroupPayload, password })
				.then(() => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return;
					successCount += 1;
				})
				.catch((e) => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return;
					failures.push({ brand, brandName: getBrandName(brand), error: this.getRequestErrorText(e, "failed") });
				});
		};

		const runDisable = (brand) => {
			if (!this.isCurrentUserContext(actionUserContextVersion)) return Promise.resolve();
			return this.props.Interface
				.request("set", { class: "settings", module: "staff", method: "revokeBrand" }, { login: person.login, brand })
				.then(() => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return;
					successCount += 1;
				})
				.catch((e) => {
					if (!this.isCurrentUserContext(actionUserContextVersion)) return;
					failures.push({ brand, brandName: getBrandName(brand), error: this.getRequestErrorText(e, "failed") });
				});
		};

		let chain = Promise.resolve();
		selectedBrands.forEach((brand) => {
			chain = chain.then(() => {
				if (mode === "grant" || mode === "restore") return runGrant(brand);
				if (mode === "disable") return runDisable(brand);
				return runUpdate(brand);
			});
		});

		chain.then(() => {
			if (!this.isCurrentUserContext(actionUserContextVersion)) return;
			this.updateModal((modal) => ({
				dirty: modal.dirty || successCount > 0,
				accessActionForm:
					successCount > 0 && !failures.length
						? { ...this.getDefaultAccessActionForm(), mode: mode === "disable" ? "update" : mode }
						: modal.accessActionForm,
				pending: { ...this.getPending(modal), accessAction: false },
			}));
			if (failures.length) {
				this.props.Interface.addNoty(
					"Error",
					"Access action finished with failures. Successful brands: " + successCount + ". Failures: " + this.formatBatchFailures(failures)
				);
			} else {
				this.props.Interface.addNoty("Success", "Access action completed for " + successCount + " brand" + (successCount === 1 ? "" : "s"));
			}
			if (successCount > 0) {
				this.setState({ showAccessPassword: false });
				this.loadPerson();
			}
		});
	}

	onSaveCredentials() {
		const state = this.props.staffDetailModal;
		const person = state.person;
		if (!person) return;
		const form = state.credentialsForm || {};
		const newLogin = (form.newLogin || "").trim();
		const password = form.password || "";
		const passwordScope = form.passwordScope || "global";
		const hasNewLogin = newLogin.length > 0;
		const hasPassword = password.length > 0;

		if (!hasNewLogin && !hasPassword) {
			this.props.Interface.addNoty("Info", "No changes to save");
			return;
		}
		if (hasNewLogin) {
			if (this.props.user && this.props.user.login && person.login === this.props.user.login) {
				this.props.Interface.addNoty("Error", "Cannot rename yourself");
				return;
			}
			if (newLogin.length < 4 || newLogin.length > 64) {
				this.props.Interface.addNoty("Error", "New login must be 4..64 characters");
				return;
			}
			if (newLogin === person.login) {
				this.props.Interface.addNoty("Error", "New login is identical to the current one");
				return;
			}
		}
		const requestPayload = { login: person.login };
		if (hasNewLogin) {
			requestPayload.newLogin = newLogin;
		}
		if (hasPassword) {
			if (password.length < 8 || password.length > 128) {
				this.props.Interface.addNoty("Error", "Password must be 8..128 characters");
				return;
			}
			if (passwordScope !== "global" && passwordScope !== "brands") {
				this.props.Interface.addNoty("Error", "Invalid scope");
				return;
			}
			requestPayload.password = password;
			requestPayload.passwordScope = passwordScope;
			if (passwordScope === "brands") {
				const selectedBrands = [];
				(form.passwordBrands || []).forEach((option) => {
					const brand = Number(option.value);
					if (Number.isInteger(brand) && brand > 0 && !selectedBrands.includes(brand)) {
						selectedBrands.push(brand);
					}
				});
				if (!selectedBrands.length) {
					this.props.Interface.addNoty("Error", "Select at least one brand for selected brands password change");
					return;
				}
				requestPayload.passwordBrands = selectedBrands;
			}
		}
		this.updateModal((modal) => ({
			pending: { ...this.getPending(modal), saveCredentials: true },
		}));
		const actionUserContextVersion = this.userContextVersion;
		this.props.Interface.request("set", { class: "settings", module: "staff", method: "saveCredentials" }, requestPayload)
			.then((resp) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return null;
				const effectiveLogin = resp && resp.login ? resp.login : hasNewLogin ? newLogin : person.login;
				this.props.Interface.addNoty("Success", "Credentials saved");
				this.updateModal((modal) => ({
					dirty: true,
					login: effectiveLogin,
					credentialsForm: { ...modal.credentialsForm, newLogin: "", password: "", passwordBrands: [] },
					pending: { ...this.getPending(modal), saveCredentials: false },
				}));
				this.setState({ showCredentialsPassword: false });
				this.loadPerson(effectiveLogin);
			})
			.catch((e) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.props.Interface.addNoty("Error", (e && e.text) || "Failed to save credentials");
				this.updateModal((modal) => ({
					pending: { ...this.getPending(modal), saveCredentials: false },
				}));
			});
	}

	onHideTfa() {
		this.updateModal({ tfaReveal: null });
	}

	onShowTfa() {
		const state = this.props.staffDetailModal;
		const person = state.person;
		if (!person) return;
		this.updateModal((modal) => ({
			pending: { ...this.getPending(modal), getTfa: true },
		}));
		const actionUserContextVersion = this.userContextVersion;
		this.props.Interface.request("get", { class: "settings", module: "staff", method: "getTfa" }, { login: person.login })
			.then((resp) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.updateModal((modal) => ({
					tfaReveal: {
						secret: resp && resp.secret ? resp.secret : null,
						otpauth: resp && resp.otpauth ? resp.otpauth : null,
						brands: resp && Array.isArray(resp.brands) ? resp.brands : [],
						loaded: true,
					},
					pending: { ...this.getPending(modal), getTfa: false },
				}));
			})
			.catch((e) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				const errorText = (e && e.text) || "Failed to reveal 2FA";
				const isInconsistent = errorText.indexOf("Inconsistent 2FA state") !== -1;
				const userText = isInconsistent ? errorText + ". Use Reset/Create 2FA to repair persona 2FA." : errorText;
				this.props.Interface.addNoty("Error", userText);
				this.updateModal((modal) => ({
					tfaReveal: {
						error: userText,
						loaded: true,
					},
					pending: { ...this.getPending(modal), getTfa: false },
				}));
			});
	}

	onResetTfaGlobal() {
		const state = this.props.staffDetailModal;
		const person = state.person;
		if (!person) return;
		const actionLabel = person.hasTfa ? "Reset" : "Create";
		// eslint-disable-next-line no-alert
		if (!window.confirm(actionLabel + " shared persona 2FA secret for " + person.login + "? All affected brand sessions will be expired.")) return;
		this.updateModal((modal) => {
			const pending = this.getPending(modal);
			return {
				pending: { ...pending, resetTfa: { ...pending.resetTfa, global: true } },
			};
		});
		const actionUserContextVersion = this.userContextVersion;
		this.props.Interface.request("set", { class: "settings", module: "staff", method: "resetTfa" }, { login: person.login, scope: "global" })
			.then((resp) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.props.Interface.addNoty("Success", "Persona 2FA secret " + (person.hasTfa ? "regenerated" : "created"));
				this.updateModal((modal) => {
					const pending = this.getPending(modal);
					const nextResetTfa = { ...pending.resetTfa };
					delete nextResetTfa.global;
					return {
						dirty: true,
						tfaReveal: {
							secret: resp && resp.secret ? resp.secret : null,
							otpauth: null,
							brands: resp && Array.isArray(resp.brands) ? resp.brands : [],
							loaded: true,
						},
						pending: { ...pending, resetTfa: nextResetTfa },
					};
				});
				this.loadPerson();
			})
			.catch((e) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.props.Interface.addNoty("Error", (e && e.text) || "Failed to reset persona 2FA");
				this.updateModal((modal) => {
					const pending = this.getPending(modal);
					const nextResetTfa = { ...pending.resetTfa };
					delete nextResetTfa.global;
					return {
						pending: { ...pending, resetTfa: nextResetTfa },
					};
				});
			});
	}

	onRemoveTfaGlobal() {
		const state = this.props.staffDetailModal;
		const person = state.person;
		if (!person || !person.hasTfa) return;
		// eslint-disable-next-line no-alert
		if (
			!window.confirm(
				"Remove 2FA for " +
					person.login +
					"? This disables the authenticator requirement for this persona across all brand access rows. Active sessions will be expired.",
			)
		) {
			return;
		}
		this.updateModal((modal) => ({
			pending: { ...this.getPending(modal), removeTfa: true },
		}));
		const actionUserContextVersion = this.userContextVersion;
		this.props.Interface.request("set", { class: "settings", module: "staff", method: "removeTfa" }, { login: person.login })
			.then(() => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.props.Interface.addNoty("Success", "Persona 2FA removed");
				this.updateModal((modal) => ({
					dirty: true,
					tfaReveal: null,
					pending: { ...this.getPending(modal), removeTfa: false },
				}));
				this.loadPerson();
			})
			.catch((e) => {
				if (!this.isCurrentUserContext(actionUserContextVersion)) return;
				this.props.Interface.addNoty("Error", (e && e.text) || "Failed to remove persona 2FA");
				this.updateModal((modal) => ({
					pending: { ...this.getPending(modal), removeTfa: false },
				}));
			});
	}

	onOpenSessions(project) {
		if (!project || !project.userId) {
			this.props.Interface.addNoty("Error", "No user id");
			return;
		}
		const brand = Number(project.brand);
		if (!Number.isInteger(brand) || brand < 1) {
			this.props.Interface.addNoty("Error", "Brand is required to load sessions");
			return;
		}
		const base = this.props.staffSessions;
		const requestPayload = { uid: project.userId, brand };
		this.props.setStaffSessions({
			...base,
			show: true,
			uid: project.userId,
			brand,
			brandName: project.brandName || "",
			loaded: false,
			data: [],
			pagination: { current: 1, total: 0 },
			error: { text: "", code: 0, timestamp: 0 },
		});
		// StaffSessionsModal has no componentDidMount/componentDidUpdate fetch path
		// (see src/modules/StaffSessionsModal.js), so we must drive the load from here.
		const perPage = (this.props.config && this.props.config.staff && this.props.config.staff.sessions && this.props.config.staff.sessions.perPage) || 15;
		this.props.Interface.request("get", { class: "data", module: "staff", method: "sessions" }, requestPayload)
			.then((data) => {
				const arr = Array.isArray(data) ? data : [];
				this.props.setStaffSessions((current) => ({
					...current,
					loaded: true,
					data: arr,
					pagination: { current: 1, total: Math.ceil(arr.length / perPage) },
				}));
			})
			.catch((e) => {
				this.props.setStaffSessions((current) => ({
					...current,
					loaded: true,
					error: e,
				}));
			});
	}

	renderCredentials() {
		const state = this.props.staffDetailModal;
		const { loaded, error, person } = state;

		if (loaded && error && error.text && error.text.length) {
			return (
				<Box margin={{ top: "m" }}>
					<Alert type="error" header="Error loading data">
						<Box variant="p">{error.text}</Box>
					</Alert>
				</Box>
			);
		}
		if (!loaded) {
			return (
				<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
					<Spinner size="large" />
				</Box>
			);
		}
		if (!person) return <></>;

		const isSelf = this.props.user && this.props.user.login && person.login === this.props.user.login;
		const pending = this.getPending(state);
		const form = state.credentialsForm || {};
		const brandOptions = (person.projects || []).map((p) => ({ value: String(p.brand), label: p.brandName }));
		const passwordBrandPlaceholder = brandOptions.length ? "Choose brands" : "No granted brands";
		const savingCredentials = pending.saveCredentials || pending.changeLogin || pending.changePassword;
		const credentialBrandOptionByValue = (value) => brandOptions.find((option) => option.value === String(value));
		const selectedPasswordBrands = (form.passwordBrands || [])
			.map((option) => option && credentialBrandOptionByValue(option.value))
			.filter((option) => !!option);
		const credentialBrandOptionsWithSelectAll = this.optionsWithSelectAll(brandOptions);
		const selectedPasswordBrandsWithSelectAll = this.selectedOptionsWithSelectAll(brandOptions, selectedPasswordBrands);
		const credentialsSection = (
			<Container
				header={
					<Header variant="h3" description="Rename login and/or set a new password in one credentials save.">
						Save credentials
					</Header>
				}>
				<SpaceBetween size="s">
					{isSelf ? <Alert type="info">You cannot rename yourself, but you can still change your password.</Alert> : null}
					<ColumnLayout columns={2}>
						<FormField label="Current login">
							<Input value={person.login} readOnly={true} />
						</FormField>
						<FormField label="New login (optional, 4..64)">
							<Input
								value={form.newLogin}
								disabled={isSelf || savingCredentials}
								onChange={(e) =>
									this.updateModal((modal) => ({
										credentialsForm: { ...modal.credentialsForm, newLogin: e.detail.value },
									}))
								}
							/>
						</FormField>
						<FormField label="New password">
							<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
								<Input
									type={this.state.showCredentialsPassword ? "text" : "password"}
									value={form.password}
									disabled={savingCredentials}
									onChange={(e) =>
										this.updateModal((modal) => ({
											credentialsForm: { ...modal.credentialsForm, password: e.detail.value },
										}))
									}
								/>
								<Button wrapText={false} onClick={() => this.setState({ showCredentialsPassword: !this.state.showCredentialsPassword })}>
									{this.state.showCredentialsPassword ? "Hide" : "Show"}
								</Button>
							</Grid>
						</FormField>
						<FormField label="Scope">
							<RadioGroup
								value={form.passwordScope}
								items={[
									{ value: "global", label: "All brands (global)" },
									{ value: "brands", label: "Selected brands" },
								]}
								onChange={(e) =>
									this.updateModal((modal) => ({
										credentialsForm: { ...modal.credentialsForm, passwordScope: e.detail.value },
									}))
								}
							/>
						</FormField>
					</ColumnLayout>
					{form.passwordScope === "brands" ? (
						<>
							<FormField label="Brands">
								<Multiselect
									options={credentialBrandOptionsWithSelectAll}
									selectedOptions={selectedPasswordBrandsWithSelectAll}
									hideTokens
									placeholder={passwordBrandPlaceholder}
									disabled={savingCredentials || !brandOptions.length}
									onChange={(e) => {
										const nextOptions = this.resolveSelectAllChange(e.detail.selectedOptions || [], brandOptions, selectedPasswordBrands);
										this.updateModal((modal) => ({
											credentialsForm: { ...modal.credentialsForm, passwordBrands: nextOptions },
										}));
									}}
								/>
							</FormField>
							{this.renderOptionTiles(
								selectedPasswordBrands,
								savingCredentials,
								"No brands selected",
								(nextOptions) =>
									this.updateModal((modal) => ({
										credentialsForm: { ...modal.credentialsForm, passwordBrands: nextOptions },
									})),
							)}
						</>
					) : null}
					<Box textAlign="right">
						<Button variant="primary" loading={savingCredentials} disabled={savingCredentials} onClick={() => this.onSaveCredentials()}>
							Save credentials
						</Button>
					</Box>
				</SpaceBetween>
			</Container>
		);

		return (
			<SpaceBetween size="l">
				{credentialsSection}
			</SpaceBetween>
		);
	}

	renderTfa() {
		const state = this.props.staffDetailModal;
		const { loaded, error, person } = state;

		if (loaded && error && error.text && error.text.length) {
			return (
				<Box margin={{ top: "m" }}>
					<Alert type="error" header="Error loading data">
						<Box variant="p">{error.text}</Box>
					</Alert>
				</Box>
			);
		}
		if (!loaded) {
			return (
				<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
					<Spinner size="large" />
				</Box>
			);
		}
		if (!person) return <></>;

		const pending = state.pending;
		const reveal = state.tfaReveal || {};
		const granted = person.projects || [];
		const resetPending = pending.resetTfa || {};
		const removePending = !!pending.removeTfa;
		const projectNameByBrand = {};
		granted.forEach((p) => {
			projectNameByBrand[String(p.brand)] = p.brandName;
		});
		const affectedBrands = (Array.isArray(reveal.brands) ? reveal.brands : [])
			.map((brand) => {
				if (brand && typeof brand === "object") {
					const brandId = brand.brand != null ? brand.brand : brand.id;
					const brandName = brand.brandName || brand.name || projectNameByBrand[String(brandId)];
					if (brandName) return brandName;
					return "Unknown brand";
				}
				const brandName = projectNameByBrand[String(brand)];
				return brandName || "Unknown brand";
			})
			.filter(Boolean)
			.join(", ");
		const revealSecret = reveal.otpauth || reveal.secret;
		const tfaActionLabel = person.hasTfa ? "Reset 2FA" : "Create 2FA";

		return (
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h3" description="Current 2FA state for the shared persona.">
							Persona status
						</Header>
					}>
					<SpaceBetween size="s">
						<ColumnLayout columns={2}>
							{this.renderKeyValue("Status", person.hasTfa ? <Badge color="green">Configured and shared</Badge> : <Badge color="grey">No 2FA configured</Badge>)}
							{this.renderKeyValue("Granted brands", granted.length)}
						</ColumnLayout>
						<Box variant="small" color="text-body-secondary">
							2FA is shared for the persona and applies to every brand access row for this login.
						</Box>
						{granted.length ? null : <Alert type="info">No granted brand access rows are currently listed for this persona.</Alert>}
					</SpaceBetween>
				</Container>

				<Container
					header={
						<Header variant="h3" description="Reveal the current shared persona secret.">
							Show secret
						</Header>
					}>
					<SpaceBetween size="s">
						<Grid gridDefinition={[{ colspan: 4 }]}>
							<FormField label="Action">
								{reveal.loaded ? (
									<Button wrapText={false} onClick={() => this.onHideTfa()}>
										Hide
									</Button>
								) : (
									<Button wrapText={false} loading={!!pending.getTfa} disabled={!!pending.getTfa} onClick={() => this.onShowTfa()}>
										Show secret
									</Button>
								)}
							</FormField>
						</Grid>
						{reveal.loaded && reveal.error ? (
							<Alert type="error" header="Unable to reveal persona 2FA">
								{reveal.error}
							</Alert>
						) : null}
						{reveal.loaded && !reveal.error && !reveal.secret ? <Alert type="info">No 2FA configured for this persona. Use Reset/Create 2FA to generate a shared secret.</Alert> : null}
						{reveal.loaded && reveal.secret ? (
							<SpaceBetween size="xs">
								<Box variant="small" color="text-body-secondary">
									{reveal.otpauth ? "Current persona 2FA URI." : "New persona 2FA secret. Add it to the authenticator app."}
									{" This shared secret applies to all brand access rows for the persona."}
								</Box>
								<pre style={{ margin: 0, wordBreak: "break-all", whiteSpace: "pre-wrap" }}>{revealSecret}</pre>
								<Box>
									<Button
										iconName="copy"
										onClick={() => {
											if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
												navigator.clipboard.writeText(revealSecret).then(
													() => this.props.Interface.addNoty("Success", reveal.otpauth ? "otpauth copied" : "secret copied"),
													() => this.props.Interface.addNoty("Error", "Clipboard unavailable"),
												);
											} else {
												this.props.Interface.addNoty("Error", "Clipboard unavailable");
											}
										}}>
										Copy
									</Button>
								</Box>
								{affectedBrands ? (
									<Box variant="small" color="text-body-secondary">
										Affected brands: {affectedBrands}
									</Box>
								) : null}
							</SpaceBetween>
						) : null}
					</SpaceBetween>
				</Container>

				<Container
					header={
						<Header variant="h3" description="Create or regenerate the shared 2FA secret for this persona.">
							{tfaActionLabel}
						</Header>
					}>
					<SpaceBetween size="m">
						<Alert type="warning" header="Risky action">
							<SpaceBetween size="s">
								<Box variant="small" color="text-body-secondary">
									This creates one shared secret for all brand access rows of the persona and expires affected brand sessions.
								</Box>
								<SpaceBetween direction="horizontal" size="xs">
									<Button wrapText={false} loading={!!resetPending.global} disabled={!!resetPending.global} onClick={() => this.onResetTfaGlobal()}>
										{tfaActionLabel}
									</Button>
								</SpaceBetween>
							</SpaceBetween>
						</Alert>
						{person.hasTfa ? (
							<Alert type="error" header="Remove 2FA">
								<SpaceBetween size="s">
									<Box variant="small" color="text-body-secondary">
										Removing 2FA disables the authenticator requirement for this persona across all brand access rows. Active sessions will be expired.
									</Box>
									<SpaceBetween direction="horizontal" size="xs">
										<Button wrapText={false} loading={removePending} disabled={removePending} onClick={() => this.onRemoveTfaGlobal()}>
											Remove 2FA
										</Button>
									</SpaceBetween>
								</SpaceBetween>
							</Alert>
						) : null}
					</SpaceBetween>
				</Container>
			</SpaceBetween>
		);
	}

	renderAccess() {
		const state = this.props.staffDetailModal;
		const { loaded, error, person, groupsError } = state;

		if (loaded && error && error.text && error.text.length) {
			return (
				<Box margin={{ top: "m" }}>
					<Alert type="error" header="Error loading data">
						<Box variant="p">{error.text}</Box>
					</Alert>
				</Box>
			);
		}
		if (!loaded) {
			return (
				<Box textAlign="center" margin={{ top: "xxl", bottom: "xxl" }}>
					<Spinner size="large" />
				</Box>
			);
		}
		if (!person) return <></>;

		const isSelf = this.props.user && this.props.user.login && person.login === this.props.user.login;
		const callerLevel = Number(this.props.user.level) || 0;
		const granted = person.projects || [];
		const available = person.availableBrands || [];
		const pending = state.pending;
		const maxAssignableLevel = Math.max(0, callerLevel - 1);
		const activeProjects = granted.filter((p) => Number(p.level) !== 0);
		const disabledProjects = granted.filter((p) => Number(p.level) === 0);
		const levelOptions = [];
		for (let i = 1; i <= maxAssignableLevel; i += 1) {
			levelOptions.push({ value: String(i), label: String(i) });
		}
		const groupsUnavailable = !!groupsError || !state.groupsLoaded;
		const groupsPlaceholder = groupsError ? "Groups unavailable" : state.groupsLoaded ? "Choose groups" : "Loading...";
		const groupsLoadErrorAlert = groupsError ? (
			<Alert
				type="error"
				header="Unable to load groups"
				action={<Button onClick={() => this.loadGroups()}>Retry</Button>}>
				{groupsError}
			</Alert>
		) : null;

		const groupOptions = (state.groups || []).map((g) => ({
			value: String(g.id),
			label: g.name,
			description: g.description || undefined,
		}));
		const groupOptionById = (id) => groupOptions.find((o) => o.value === String(id)) || { value: String(id), label: "#" + id };
		const renderGroupTiles = (selected, disabled, onRemove) => {
			const groupEditingDisabled = disabled || isSelf || groupsUnavailable;
			return selected.length ? (
				<div
					style={{
						display: "grid",
						gap: "6px",
						gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
					}}>
					{selected.map((option) => (
						<div
							key={option.value}
							role="button"
							tabIndex={groupEditingDisabled ? -1 : 0}
							title={groupEditingDisabled ? option.label : "Remove " + option.label}
							aria-disabled={groupEditingDisabled}
							onClick={() => {
								if (groupEditingDisabled) return;
								const ids = selected.filter((o) => o.value !== option.value).map((o) => Number(o.value));
								onRemove(ids);
							}}
							onKeyDown={(e) => {
								if (groupEditingDisabled) return;
								if (e.key !== "Enter" && e.key !== " ") return;
								e.preventDefault();
								const ids = selected.filter((o) => o.value !== option.value).map((o) => Number(o.value));
								onRemove(ids);
							}}
							style={{
								border: "1px solid #0972d3",
								borderRadius: "8px",
								boxSizing: "border-box",
								cursor: groupEditingDisabled ? "default" : "pointer",
								display: "flex",
								gap: "8px",
								justifyContent: "space-between",
								minHeight: "36px",
								overflow: "hidden",
								padding: "7px 10px",
								width: "100%",
							}}>
							<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
							{groupEditingDisabled ? null : <span aria-hidden="true">x</span>}
						</div>
					))}
				</div>
			) : (
				<Box variant="small" color="text-body-secondary">
					No groups selected
				</Box>
			);
		};

		const actionModeOptions = [
			{ value: "update", label: "Update active access", description: "Change level, groups, or both for active brand rows." },
			{ value: "grant", label: "Grant missing access", description: "Create access on missing projects." },
			{ value: "restore", label: "Restore disabled access", description: "Re-enable disabled brand rows with a new password." },
			{ value: "disable", label: "Disable active access", description: "Disable active brand rows." },
		];
		const actionForm = { ...this.getDefaultAccessActionForm(), ...(state.accessActionForm || {}) };
		const actionMode = actionForm.mode || "update";
		const selectedActionMode = actionModeOptions.find((option) => option.value === actionMode) || actionModeOptions[0];
		const optionFromProject = (project, description) => ({
			value: String(project.brand),
			label: project.brandName,
			description,
		});
		const accessBrandOptions =
			actionMode === "grant"
				? available.map((item) => optionFromProject(item, "Missing access"))
				: actionMode === "restore"
				? disabledProjects.map((item) => optionFromProject(item, "Disabled access"))
				: activeProjects.map((item) => optionFromProject(item, "Active access"));
		const accessBrandOptionByValue = (value) => accessBrandOptions.find((option) => option.value === String(value));
		const selectedAccessBrands = (actionForm.brands || []).map((option) => accessBrandOptionByValue(option.value)).filter((option) => !!option);
		const accessBrandOptionsWithSelectAll = this.optionsWithSelectAll(accessBrandOptions);
		const selectedAccessBrandsWithSelectAll = this.selectedOptionsWithSelectAll(accessBrandOptions, selectedAccessBrands);
		const selectedAccessLevel = levelOptions.find((option) => option.value === String(actionForm.level)) || null;
		const selectedAccessGroups = (actionForm.groupIds || []).map((id) => groupOptionById(id));
		const accessPending = !!pending.accessAction;
		const accessNeedsGroupsLoaded = actionMode === "grant" || actionMode === "restore" || (actionMode === "update" && !!actionForm.updateGroups);
		const accessNeedsLevel = actionMode !== "disable" && (actionMode !== "update" || !!actionForm.updateLevel);
		const accessLevel = Number(actionForm.level);
		const accessLevelInvalid = accessNeedsLevel && (!Number.isFinite(accessLevel) || accessLevel < 1 || accessLevel >= callerLevel);
		const accessPassword = typeof actionForm.password === "string" ? actionForm.password : "";
		const accessPasswordInvalid = (actionMode === "grant" || actionMode === "restore") && (accessPassword.length < 8 || accessPassword.length > 128);
		const updateHasNoFields = actionMode === "update" && !actionForm.updateLevel && !actionForm.updateGroups;
		const accessSubmitDisabled =
			isSelf || accessPending || !selectedAccessBrands.length || updateHasNoFields || (accessNeedsGroupsLoaded && groupsUnavailable) || accessLevelInvalid || accessPasswordInvalid;
		const accessSubmitLabel =
			actionMode === "grant"
				? "Grant access"
				: actionMode === "restore"
				? "Restore access"
				: actionMode === "disable"
				? "Disable access"
				: "Update access";
		const accessBrandPlaceholder = accessBrandOptions.length
			? "Choose brands"
			: actionMode === "grant"
			? "No missing brands"
			: actionMode === "restore"
			? "No disabled brands"
			: "No active brands";
		const matrixBorderColor = "rgba(128, 128, 128, 0.35)";
		const matrixSoftBorderColor = "rgba(128, 128, 128, 0.18)";
		const matrixHeaderBackground = "rgba(128, 128, 128, 0.08)";
		const matrixSelectedBorderColor = "#0972d3";
		const groupUpdateMode = actionForm.groupUpdateMode === "add" || actionForm.groupUpdateMode === "remove" ? actionForm.groupUpdateMode : "replace";
		const groupUpdateModeOptions = [
			{ value: "replace", label: "Replace groups", description: "Set selected groups exactly. Empty selection clears groups." },
			{ value: "add", label: "Add groups", description: "Keep current groups and add selected groups." },
			{ value: "remove", label: "Remove groups", description: "Remove selected groups from current groups." },
		];
		const isMatrixItemSelected = (item) => selectedAccessBrands.some((option) => option.value === String(item.brand));
		const toggleMatrixItem = (item) => {
			const option = accessBrandOptionByValue(item.brand);
			if (!option || isSelf || accessPending || actionMode === "grant") return;
			const brands = isMatrixItemSelected(item) ? selectedAccessBrands.filter((selected) => selected.value !== option.value) : [...selectedAccessBrands, option];
			this.updateAccessActionForm({ brands });
		};
		const toggleGroupInAccessForm = (item, groupId) => {
			if (actionMode !== "update" || isSelf || accessPending || groupsUnavailable) return;
			const option = accessBrandOptionByValue(item.brand);
			const value = Number(groupId);
			const currentIds = (actionForm.groupIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id));
			const nextIds = currentIds.includes(value) ? currentIds.filter((id) => id !== value) : [...currentIds, value];
			const nextBrands = option && !isMatrixItemSelected(item) ? [...selectedAccessBrands, option] : selectedAccessBrands;
			this.updateAccessActionForm({ brands: nextBrands, updateLevel: false, updateGroups: true, groupIds: nextIds });
		};
		const renderMatrixGroupChips = (item) => {
			const groups = item.groups || [];
			if (!groups.length) {
				return (
					<Box variant="small" color="text-body-secondary">
						No groups
					</Box>
				);
			}
			return (
				<div
					style={{
						display: "grid",
						gap: "6px",
						gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
						width: "100%",
					}}>
					{groups.map((group) => {
						const selected = (actionForm.groupIds || []).some((id) => Number(id) === Number(group.id));
						const clickable = actionMode === "update" && !isSelf && !accessPending && !groupsUnavailable;
						return (
							<span
								key={group.id}
								role={clickable ? "button" : undefined}
								tabIndex={clickable ? 0 : undefined}
								title={clickable ? "Use " + group.name + " in group operation" : group.name}
								onClick={() => toggleGroupInAccessForm(item, group.id)}
								onKeyDown={(e) => {
									if (!clickable) return;
									if (e.key !== "Enter" && e.key !== " ") return;
									e.preventDefault();
									toggleGroupInAccessForm(item, group.id);
								}}
								style={{
									border: "1px solid " + (selected ? matrixSelectedBorderColor : matrixBorderColor),
									borderRadius: "12px",
									boxSizing: "border-box",
									cursor: clickable ? "pointer" : "default",
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-start",
									lineHeight: "18px",
									minHeight: "28px",
									minWidth: 0,
									overflow: "hidden",
									padding: "3px 8px",
									textAlign: "left",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									width: "100%",
								}}>
								<span style={{ display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{group.name || "#" + group.id}
								</span>
							</span>
						);
					})}
				</div>
			);
		};
		const groupsField = (
			<SpaceBetween size="xs">
				<FormField label={actionMode === "update" ? "Groups" : "Groups (optional)"}>
					<Multiselect
						options={groupOptions}
						selectedOptions={selectedAccessGroups}
						hideTokens
						placeholder={groupsPlaceholder}
						disabled={isSelf || accessPending || groupsUnavailable}
						onChange={(e) => {
							const ids = (e.detail.selectedOptions || []).map((option) => Number(option.value));
							this.updateAccessActionForm({ groupIds: ids });
						}}
					/>
				</FormField>
				{renderGroupTiles(selectedAccessGroups, accessPending, (ids) => this.updateAccessActionForm({ groupIds: ids }))}
			</SpaceBetween>
		);
		const accessActionSection = (
			<Container
				header={
					<Header variant="h3" description="Choose one or more projects and apply the same access action to them.">
						Access action
					</Header>
				}>
				<SpaceBetween size="s">
					{isSelf ? <Alert type="info">You cannot modify your own per-brand access.</Alert> : null}
					<FormField label="Mode">
						<Select
							options={actionModeOptions}
							selectedOption={selectedActionMode}
							disabled={accessPending}
							onChange={(e) => {
								const nextMode = e.detail.selectedOption.value;
								this.updateAccessActionForm({
									mode: nextMode,
									brands: [],
									level: "",
									password: "",
									groupIds: [],
									updateLevel: false,
									updateGroups: nextMode === "update",
									groupUpdateMode: "replace",
								});
							}}
						/>
					</FormField>
					<FormField label="Brands">
						<Multiselect
							options={accessBrandOptionsWithSelectAll}
							selectedOptions={selectedAccessBrandsWithSelectAll}
							hideTokens
							placeholder={accessBrandPlaceholder}
							disabled={isSelf || accessPending || !accessBrandOptions.length}
							onChange={(e) => {
								const nextOptions = this.resolveSelectAllChange(e.detail.selectedOptions || [], accessBrandOptions, selectedAccessBrands);
								this.updateAccessActionForm({ brands: nextOptions });
							}}
						/>
					</FormField>
					{this.renderOptionTiles(
						selectedAccessBrands,
						isSelf || accessPending,
						"No brands selected",
						(nextOptions) => this.updateAccessActionForm({ brands: nextOptions }),
					)}
					{actionMode === "disable" ? (
						<Alert type="warning" header="This disables access">
							This disables access, clears assigned groups, and expires active sessions.
						</Alert>
					) : (
						<SpaceBetween size="xs">
							{actionMode === "update" ? (
								<SpaceBetween direction="horizontal" size="l">
									<Checkbox
										checked={!!actionForm.updateLevel}
										disabled={isSelf || accessPending}
										onChange={(e) => this.updateAccessActionForm({ updateLevel: e.detail.checked })}>
										Replace level
									</Checkbox>
									<Checkbox
										checked={!!actionForm.updateGroups}
										disabled={isSelf || accessPending}
										onChange={(e) => this.updateAccessActionForm({ updateGroups: e.detail.checked })}>
										Update groups
									</Checkbox>
								</SpaceBetween>
							) : null}
							{actionMode === "update" && actionForm.updateGroups ? (
								<FormField label="Group operation">
									<RadioGroup
										value={groupUpdateMode}
										items={groupUpdateModeOptions}
										onChange={(e) => this.updateAccessActionForm({ groupUpdateMode: e.detail.value })}
									/>
								</FormField>
							) : null}
							{accessNeedsLevel || actionMode === "grant" || actionMode === "restore" ? (
								<ColumnLayout columns={actionMode === "grant" || actionMode === "restore" ? 2 : 1}>
									{accessNeedsLevel ? (
										<FormField label={`Level (1..${maxAssignableLevel})`}>
											<Select
												options={levelOptions}
												selectedOption={selectedAccessLevel}
												placeholder="Choose level"
												disabled={isSelf || accessPending}
												onChange={(e) => this.updateAccessActionForm({ level: e.detail.selectedOption.value })}
											/>
										</FormField>
									) : null}
									{actionMode === "grant" || actionMode === "restore" ? (
										<FormField label="Password (8..128)">
											<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
												<Input
													type={this.state.showAccessPassword ? "text" : "password"}
													value={actionForm.password}
													disabled={isSelf || accessPending}
													onChange={(e) => this.updateAccessActionForm({ password: e.detail.value })}
												/>
												<Button wrapText={false} onClick={() => this.setState({ showAccessPassword: !this.state.showAccessPassword })}>
													{this.state.showAccessPassword ? "Hide" : "Show"}
												</Button>
											</Grid>
										</FormField>
									) : null}
								</ColumnLayout>
							) : null}
							{actionMode === "grant" || actionMode === "restore" || actionForm.updateGroups ? groupsField : null}
						</SpaceBetween>
					)}
					<Box textAlign="right">
						<Button wrapText={false} variant="primary" loading={accessPending} disabled={accessSubmitDisabled} onClick={() => this.onSubmitAccessAction()}>
							{accessSubmitLabel}
						</Button>
					</Box>
				</SpaceBetween>
			</Container>
		);
		const matrixColumns = "96px minmax(220px, 1fr) 96px 96px";
		const matrixCellStyle = {
			alignItems: "center",
			borderRight: "1px solid " + matrixBorderColor,
			display: "flex",
			minHeight: "48px",
			padding: "0 16px",
		};
		const matrixHeaderCellStyle = {
			...matrixCellStyle,
			background: matrixHeaderBackground,
			fontWeight: 700,
		};
		const currentAccessMatrixSection = (
			<ExpandableSection
				headerText={"Current access matrix (" + granted.length + ")"}
				expanded={this.state.accessMatrixExpanded}
				onChange={(e) => this.setState({ accessMatrixExpanded: e && e.detail ? e.detail.expanded : !this.state.accessMatrixExpanded })}>
				<SpaceBetween size="s">
					<Box variant="small" color="text-body-secondary">
						Select rows to target the action above; group chips show current assignments.
					</Box>
					{actionMode === "grant" ? <Alert type="info">Missing brands are not in current access yet. Select them in the Brands field above.</Alert> : null}
					{granted.length ? (
						<div style={{ border: "1px solid " + matrixBorderColor, borderRadius: "16px", overflow: "hidden" }}>
							<div
								style={{
									borderBottom: "1px solid " + matrixBorderColor,
									display: "grid",
									gridTemplateColumns: matrixColumns,
								}}>
								<div style={matrixHeaderCellStyle}>
									<span style={{ whiteSpace: "nowrap" }}>Select</span>
								</div>
								<div style={matrixHeaderCellStyle}>
									<span style={{ whiteSpace: "nowrap" }}>Brand</span>
								</div>
								<div style={matrixHeaderCellStyle}>
									<span style={{ whiteSpace: "nowrap" }}>Status</span>
								</div>
								<div style={{ ...matrixHeaderCellStyle, borderRight: "none" }}>
									<span style={{ whiteSpace: "nowrap" }}>Level</span>
								</div>
							</div>
							{granted.map((item) => {
								const selectable = !!accessBrandOptionByValue(item.brand) && !isSelf && !accessPending && actionMode !== "grant";
								return (
									<div key={item.brand} style={{ borderBottom: "1px solid " + matrixBorderColor }}>
										<div
											style={{
												borderBottom: "1px solid " + matrixSoftBorderColor,
												display: "grid",
												gridTemplateColumns: matrixColumns,
											}}>
											<div style={matrixCellStyle}>
												<Checkbox checked={isMatrixItemSelected(item)} disabled={!selectable} onChange={() => toggleMatrixItem(item)} />
											</div>
											<div style={matrixCellStyle}>
												<Box variant="span">{item.brandName}</Box>
											</div>
											<div style={matrixCellStyle}>
												{Number(item.level) === 0 ? <Badge color="grey">disabled</Badge> : <Badge color="green">active</Badge>}
											</div>
											<div style={{ ...matrixCellStyle, borderRight: "none" }}>
												<Box variant="span">{item.level}</Box>
											</div>
										</div>
										<div style={{ padding: "14px 16px" }}>
											<Box variant="awsui-key-label">Groups</Box>
											<Box margin={{ top: "xs" }}>{renderMatrixGroupChips(item)}</Box>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<Box variant="span">No current access rows</Box>
					)}
				</SpaceBetween>
			</ExpandableSection>
		);
		return (
			<SpaceBetween size="l">
				{groupsLoadErrorAlert}
				{accessActionSection}
				{currentAccessMatrixSection}
			</SpaceBetween>
		);
	}

	render() {
		const { login, activeTabId, person, loaded } = this.props.staffDetailModal;
		const headerText = loaded && person ? `Staff: ${person.login}` : login ? `Staff: ${login}` : "Staff";

		return (
			<Modal
				size="large"
				visible={this.props.staffDetailModal.show}
				onDismiss={() => this.onDismiss()}
				header={<Header>{headerText}</Header>}>
				<Tabs
					activeTabId={activeTabId}
					onChange={(tab) => {
						const next = tab.detail.activeTabId;
						this.updateModal({ activeTabId: next });
						if (next === "access" && !this.props.staffDetailModal.groupsLoaded && !this.props.staffDetailModal.groupsError) {
							this.loadGroups();
						}
					}}
					tabs={[
						{ id: "overview", label: "Overview", content: this.renderOverview() },
						{
							id: "access",
							label: "Access",
							content: this.renderAccess(),
						},
						{
							id: "credentials",
							label: "Credentials",
							content: this.renderCredentials(),
						},
						{
							id: "tfa",
							label: "2FA",
							content: this.renderTfa(),
						},
					]}
				/>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const config = useRecoilValue(configStore);
		const staff = useRecoilValue(staffStore);
		const setStaff = useSetRecoilState(staffStore);
		const staffDetailModal = useRecoilValue(staffDetailModalStore);
		const setStaffDetailModal = useSetRecoilState(staffDetailModalStore);
		const staffSessions = useRecoilValue(staffSessionsModalStore);
		const setStaffSessions = useSetRecoilState(staffSessionsModalStore);

		return (
			<Component
				{...props}
				user={user}
				config={config}
				staff={staff}
				setStaff={setStaff}
				staffDetailModal={staffDetailModal}
				setStaffDetailModal={setStaffDetailModal}
				staffSessions={staffSessions}
				setStaffSessions={setStaffSessions}
			/>
		);
	};
}

export default withRecoil(StaffDetailModal);
